import axios from 'axios';

class ApiClient {
  constructor() {
    const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

    // Heal poisoned storage: a client that treated the OTP challenge (202,
    // no tokens) as a normal login once stored the literal string
    // "undefined" here, and JSON.parse of it crashed the app at boot.
    try {
      const keys = ['admin_access_token', 'admin_refresh_token', 'admin_user', 'admin_hotel_id'];
      if (keys.some((k) => localStorage.getItem(k) === 'undefined')) {
        keys.forEach((k) => localStorage.removeItem(k));
      }
    } catch (e) { /* storage unavailable */ }

    this.client = axios.create({
      baseURL,
      headers: { 'Content-Type': 'application/json' },
    });

    // Add auth token + hotel context to every request
    this.client.interceptors.request.use((config) => {
      const token = localStorage.getItem('admin_access_token');
      if (token) config.headers.Authorization = `Bearer ${token}`;

      const hotelId = localStorage.getItem('admin_hotel_id');
      if (hotelId) config.headers['X-Hotel-Context'] = hotelId;

      return config;
    });

    // Auto-refresh on 401
    this.client.interceptors.response.use(
      (res) => res,
      async (error) => {
        const original = error.config;
        if (error.response?.status === 401 && !original._retry) {
          original._retry = true;
          try {
            const refresh = localStorage.getItem('admin_refresh_token');
            if (refresh) {
              const res = await axios.post(`${baseURL}/auth/token/refresh/`, { refresh });
              localStorage.setItem('admin_access_token', res.data.access);
              original.headers.Authorization = `Bearer ${res.data.access}`;
              return this.client(original);
            }
          } catch {
            this.logout();
            window.location.href = '/login';
          }
        }
        return Promise.reject(error);
      }
    );
  }

  // Auth
  // captchaToken: optional Cloudflare Turnstile token (sent as cf_turnstile_token).
  async login(username, password, captchaToken, otp) {
    const payload = { username, password };
    if (captchaToken) payload.cf_turnstile_token = captchaToken;
    // Admin-panel accounts are elevated by definition, so the server asks
    // for an emailed one-time code once every 24h. A previous verification
    // left a signed trust token; send it to skip the code in that window.
    try {
      const trust = localStorage.getItem('admin_otp_trust');
      if (trust) payload.otp_trust = trust;
    } catch (e) { /* storage unavailable — the server will just challenge */ }
    if (otp && otp.code) {
      payload.otp_code = otp.code;
      payload.otp_challenge = otp.challenge;
    }
    const res = await this.client.post('/auth/token/', payload);
    if (res.status === 202 || res.data?.otp_required) {
      return {
        otpRequired: true,
        challenge: res.data.otp_challenge,
        emailHint: res.data.email_hint || '',
      };
    }
    const { access, refresh, user, otp_trust } = res.data;
    if (!access || !refresh || !user) {
      // Never write a missing value into storage — setItem would coerce it
      // to the string "undefined" and poison every later boot.
      throw new Error('Unexpected sign-in response. Please try again.');
    }
    if (otp_trust) {
      try { localStorage.setItem('admin_otp_trust', otp_trust); } catch (e) { /* nicety only */ }
    }
    localStorage.setItem('admin_access_token', access);
    localStorage.setItem('admin_refresh_token', refresh);
    localStorage.setItem('admin_user', JSON.stringify(user));
    const hotelId = user?.user_profile?.hotel?.id;
    if (hotelId) localStorage.setItem('admin_hotel_id', String(hotelId));
    return res.data;
  }

  // Superadmin only: every hotel, newest first, with lead-spotting fields.
  async superadminHotels() {
    const res = await this.client.get('/auth/superadmin/hotels/');
    return res.data;
  }

  // Superadmin only: enter a hotel as its admin on a brand-new session.
  // The old refresh token goes up for revocation, then BOTH web storages
  // are wiped before the new pair is stored — nothing cached from the
  // previous property survives. Caller reloads the app.
  async superadminSwitchHotel(hotelId) {
    const oldRefresh = localStorage.getItem('admin_refresh_token');
    const res = await this.client.post('/auth/superadmin/switch-hotel/', {
      hotel_id: hotelId,
      refresh: oldRefresh,
    });
    const { access, refresh, user } = res.data || {};
    if (!access || !refresh || !user) {
      throw new Error('Unexpected switch response. Please try again.');
    }
    try {
      localStorage.clear();
      sessionStorage.clear();
    } catch (e) { /* storage unavailable */ }
    localStorage.setItem('admin_access_token', access);
    localStorage.setItem('admin_refresh_token', refresh);
    localStorage.setItem('admin_user', JSON.stringify(user));
    const newHotelId = user?.user_profile?.hotel?.id;
    if (newHotelId) localStorage.setItem('admin_hotel_id', String(newHotelId));
    return user;
  }

  logout() {
    localStorage.removeItem('admin_access_token');
    localStorage.removeItem('admin_refresh_token');
    localStorage.removeItem('admin_user');
    localStorage.removeItem('admin_hotel_id');
  }

  isAuthenticated() {
    const token = localStorage.getItem('admin_access_token');
    return !!token && token !== 'undefined';
  }

  getUser() {
    try {
      const u = localStorage.getItem('admin_user');
      if (!u || u === 'undefined' || u === 'null') return null;
      return JSON.parse(u);
    } catch (e) {
      localStorage.removeItem('admin_user');
      return null;
    }
  }

  getHotelId() {
    return localStorage.getItem('admin_hotel_id');
  }

  // HTTP helpers — return response.data directly
  async get(url, params) {
    const res = await this.client.get(url, { params });
    return res.data;
  }

  async post(url, data) {
    const res = await this.client.post(url, data);
    return res.data;
  }

  // The shared axios instance pins Content-Type: application/json, which
  // would stop the browser writing a multipart boundary. Undefined lets it.
  async postForm(url, formData) {
    const res = await this.client.post(url, formData, {
      headers: { 'Content-Type': undefined },
    });
    return res.data;
  }

  async put(url, data) {
    const res = await this.client.put(url, data);
    return res.data;
  }

  // Multipart counterpart of put() — see postForm for why Content-Type
  // must be left undefined.
  async putForm(url, formData) {
    const res = await this.client.put(url, formData, {
      headers: { 'Content-Type': undefined },
    });
    return res.data;
  }

  async patch(url, data) {
    const res = await this.client.patch(url, data);
    return res.data;
  }

  async del(url) {
    const res = await this.client.delete(url);
    return res.data;
  }
}

const apiClient = new ApiClient();
export default apiClient;
